import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import Image from "../components/Image"
import { ArrowRight, CheckCircle, Globe, Users, Wrench, Zap } from "lucide-react"

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 py-24 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Australia's Premier Specialists
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="text-red-500">One Machine,</span>
              <br />
              <span className="text-white">Multiple Applications</span>
              <br />
              <span className="text-2xl md:text-3xl lg:text-4xl text-gray-300">MPV Multipurpose Vehicles</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Over two decades of expertise in multipurpose vehicles and heavy machinery attachments. Engineered for
              municipal, industrial, and commercial applications worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/models"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-flex items-center justify-center"
              >
                Explore Our Range
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/contact"
                className="border border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-flex items-center justify-center"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="mb-4 inline-block bg-red-100 text-red-800 hover:bg-red-200 px-3 py-1 rounded-full text-sm font-medium">
                  Our Story
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Transforming Industries Through Innovation
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  MPV began with a bold vision: to transform the way multipurpose vehicles serve industries that demand
                  flexibility, durability, and innovation. Over the past 20 years, we've grown into a trusted name in
                  sectors such as municipal services, mining, airport operations, road maintenance, and facilities
                  management.
                </p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Our journey has been fuelled by our relentless pursuit of excellence and a deep understanding of what
                  our clients truly need—reliable, adaptable equipment that performs in every condition.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-gray-700">Municipal Services</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-gray-700">Mining Operations</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-gray-700">Airport Operations</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-gray-700">Road Maintenance</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">
                    MPV gives you more than a product—it gives you a competitive edge.
                  </h3>
                  <p className="text-red-100 mb-6">
                    Step into a partnership that sets you apart. With industry-leading innovation, unmatched
                    versatility, and proven performance, MPV positions you ahead of the competition.
                  </p>
                  <Link
                    to="/partners"
                    className="bg-white text-red-600 hover:bg-gray-100 px-6 py-3 rounded-lg font-medium transition-colors inline-block"
                  >
                    Partner With Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multihog Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="mb-4 inline-block bg-red-100 text-red-800 hover:bg-red-200 px-3 py-1 rounded-full text-sm font-medium">
                Our Flagship Product
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">The Multihog Tractor Range</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Meet The Multihog, originally designed as a machine to cut grass on slopes and verges. Today it has
                evolved into a range of multipurpose tractors, capable of endless applications.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">The Pinnacle of Multipurpose Engineering</h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Our flagship product, the Multihog Tractor, represents the pinnacle of multipurpose engineering. This
                  machine is a versatile workhorse, designed to work with over forty specialised application-specific
                  attachments.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <Wrench className="h-6 w-6 text-red-600 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Surface Cleaners & Thermal Weed Controllers</h4>
                      <p className="text-gray-600">Advanced cleaning and maintenance solutions</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Zap className="h-6 w-6 text-red-600 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Patch Planners & High-Tip Buckets</h4>
                      <p className="text-gray-600">Precision tools for road maintenance and construction</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Globe className="h-6 w-6 text-red-600 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">All-Season Versatility</h4>
                      <p className="text-gray-600">From snow clearing to runway cleaning and urban greenery</p>
                    </div>
                  </div>
                </div>
                <Link
                  to="/models"
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center"
                >
                  View Multihog Range
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
              <div className="relative">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <Image
                    src="https://placehold.co/600x400?text=Multihog+Tractor"
                    alt="Multihog Tractor"
                    width={600}
                    height={400}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">One Machine, Multiple Applications</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Whether clearing snow, maintaining roads, cleaning runways, or tending to urban greenery, the Multihog
                offers unmatched efficiency and utility for every season and environment.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Municipal Services",
                  description: "Street cleaning, park maintenance, and urban infrastructure care",
                  icon: Users,
                  href: "/applications/municipal",
                },
                {
                  title: "Ground Care",
                  description: "Professional landscaping, sports fields, and vegetation management",
                  icon: Wrench,
                  href: "/applications/ground-care",
                },
                {
                  title: "Road Maintenance",
                  description: "Highway upkeep, surface preparation, and infrastructure repair",
                  icon: Globe,
                  href: "/applications/road-maintenance",
                },
                {
                  title: "Winter Operations",
                  description: "Snow removal, de-icing, and cold weather applications",
                  icon: Zap,
                  href: "/applications/winter",
                },
                {
                  title: "Airport Operations",
                  description: "Runway maintenance, ground support, and facility management",
                  icon: CheckCircle,
                  href: "/applications/airport",
                },
                {
                  title: "Specialized Applications",
                  description: "Custom solutions for unique industrial requirements",
                  icon: ArrowRight,
                  href: "/applications",
                },
              ].map((application, index) => (
                <Link
                  key={index}
                  to={application.href}
                  className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow block"
                >
                  <application.icon className="h-12 w-12 text-red-600 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{application.title}</h3>
                  <p className="text-gray-600">{application.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Experience the MPV Difference?</h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Join the growing number of businesses worldwide who trust MPV for their multipurpose vehicle needs. Let's
              discuss how we can help transform your operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Get a Quote
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default HomePage
